import React, { useState, useMemo } from "react";
import { Popover, Input, Empty } from "antd";
import { Search, icons } from "lucide-react";
import { getAllIconNames } from "./iconPickerData";

interface LucideIconPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  filterIcons?: string[];
}

export const LucideIconPicker: React.FC<LucideIconPickerProps> = ({
  value,
  onChange,
  filterIcons,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const allIcons = useMemo(() => getAllIconNames(), []);

  const availableIcons = useMemo(() => {
    const list = filterIcons || allIcons;
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((name) => name.toLowerCase().includes(q));
  }, [search, filterIcons, allIcons]);

  const SelectedIcon =
    value && value in icons ? icons[value as keyof typeof icons] : null;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={
        <div className="w-[340px]">
          <Input
            prefix={<Search size={14} className="text-gray-400" />}
            placeholder="Search icons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="!mb-2"
            allowClear
            size="small"
            autoFocus
          />
          <div className="max-h-[280px] overflow-y-auto">
            {availableIcons.length === 0 ? (
              <Empty
                description="No icons found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {availableIcons.map((name) => {
                  const IconComp = icons[name as keyof typeof icons];
                  const isSelected = value === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        onChange?.(name);
                        setOpen(false);
                        setSearch("");
                      }}
                      className={`
                        flex items-center justify-center w-9 h-9 rounded-md border transition-all cursor-pointer
                        ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 text-blue-600 shadow-sm"
                            : "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                        }
                      `}
                      title={name}
                    >
                      <IconComp size={16} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      }
    >
      <button
        type="button"
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-all cursor-pointer
          ${
            value
              ? "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
              : "border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600"
          }
        `}
      >
        {SelectedIcon ? <SelectedIcon size={16} /> : null}
        <span>{value || "Select icon"}</span>
      </button>
    </Popover>
  );
};
